import type { SupabaseClient } from '../supabase/client';
import type {
  Store,
  CreateStoreInput,
  StoreErrorKey,
  StagedStoreLogoUpload,
  StoreLogoPublicUrl,
  StoreLogoStoragePath,
} from '../types/store';
import {
  STORE_LOGO_BUCKET,
  assertStoreLogoContentType,
  assertStoreLogoPublicUrlForStore,
  buildVersionedStoreLogoPath,
  getSupabaseUrlFromUnknown,
  parseStoreLogoPathFromPublicUrl,
} from '../lib/store-logo-storage';
import { getFileExtension, validateImageFile } from '../lib/image-validation';
import { RESERVED_STOREFRONT_SUBDOMAINS } from '../lib/storefront-location';

/** Reserved slugs that conflict with app routes or common subdomains. */
export const RESERVED_SLUGS = new Set([
  ...RESERVED_STOREFRONT_SUBDOMAINS,
  'store',
  'stores',
  'about',
  'contact',
  'pricing',
  'terms',
  'privacy',
  'blog',
  'new',
  'create',
  'edit',
  'delete',
  'settings',
  'profile',
  'search',
]);

/**
 * Create a new store. The caller must be authenticated; the RLS policy
 * enforces owner_id = auth.uid(). The stores_after_insert_add_owner trigger
 * automatically creates the store_members row with role='owner'.
 *
 * Rev 2 fix: owner_id MUST be included in the INSERT. The column is NOT NULL,
 * and RLS only CHECKS that owner_id = auth.uid() — it does not SET the value.
 * Omitting it causes a NOT NULL constraint violation.
 *
 * @returns The created Store row.
 */
export async function createStore(client: SupabaseClient, input: CreateStoreInput): Promise<Store> {
  const { data, error } = await client.rpc('create_store', {
    store_name: input.name,
    store_description: input.description,
    store_slug: input.slug,
  });

  if (error) throw error;
  return data as Store;
}

/**
 * Update a store's logo_url after uploading to Storage.
 * Separated from createStore because the upload happens after the insert
 * (we need the store ID for the Storage path).
 */
export async function updateStoreLogoUrl(
  client: SupabaseClient,
  storeId: string,
  logoUrl: StoreLogoPublicUrl | null,
): Promise<void> {
  const supabaseUrl = getSupabaseUrlFromUnknown(client);
  const validatedLogoUrl =
    logoUrl === null ? null : assertStoreLogoPublicUrlForStore(storeId, logoUrl, supabaseUrl);
  const { error } = await client
    .from('stores')
    .update({ logo_url: validatedLogoUrl })
    .eq('id', storeId);
  if (error) throw error;
}

/**
 * Check if a slug is available by querying the stores_public view.
 * Returns true if the slug is NOT taken.
 *
 * Note: this is a UI convenience check. The DB unique index is the
 * source of truth; race conditions between this check and the insert
 * are possible but rare, and the insert error handles them.
 */
export async function checkSlugAvailable(client: SupabaseClient, slug: string): Promise<boolean> {
  const { data, error } = await client.from('stores_public').select('id').eq('slug', slug).limit(1);

  if (error) throw error;
  return (data?.length ?? 0) === 0;
}

/**
 * Upload a store logo to the store-logos bucket.
 * Path convention: {store_id}/logo.{ext}
 *
 * @returns The public URL of the uploaded logo.
 */
export async function uploadStoreLogo(
  client: SupabaseClient,
  storeId: string,
  file: File,
): Promise<StoreLogoPublicUrl> {
  const ext = getFileExtension(file.type);
  const path = `${storeId}/logo.${ext}`;

  const { error } = await client.storage.from(STORE_LOGO_BUCKET).upload(path, file, {
    upsert: true, // overwrite if re-uploading
    contentType: file.type,
  });

  if (error) throw error;

  const { data: urlData } = client.storage.from(STORE_LOGO_BUCKET).getPublicUrl(path);

  return assertStoreLogoPublicUrlForStore(
    storeId,
    urlData.publicUrl,
    getSupabaseUrlFromUnknown(client),
  );
}

export async function stageStoreLogoReplacement(
  client: SupabaseClient,
  storeId: string,
  file: File,
): Promise<StagedStoreLogoUpload> {
  const validationError = validateImageFile(file);
  if (validationError === 'imageTooLarge') throw new Error('Store logo is too large');
  if (validationError === 'imageInvalidType') throw new Error('Invalid store logo content type');

  const contentType = assertStoreLogoContentType(file.type);
  const path = buildVersionedStoreLogoPath(storeId, contentType);

  const { error } = await client.storage.from(STORE_LOGO_BUCKET).upload(path, file, {
    upsert: false,
    contentType: file.type,
  });

  if (error) throw error;

  const { data: urlData } = client.storage.from(STORE_LOGO_BUCKET).getPublicUrl(path);

  return {
    bucket: STORE_LOGO_BUCKET,
    path,
    publicUrl: assertStoreLogoPublicUrlForStore(
      storeId,
      urlData.publicUrl,
      getSupabaseUrlFromUnknown(client),
    ),
    contentType,
    sizeBytes: file.size,
  };
}

export async function deleteStoreLogoObject(
  client: SupabaseClient,
  path: StoreLogoStoragePath,
): Promise<void> {
  const { error } = await client.storage.from(STORE_LOGO_BUCKET).remove([path]);
  if (error) console.error('Failed to delete store logo object:', error);
}

export async function deleteStoreLogoByUrl(
  client: SupabaseClient,
  storeId: string,
  logoUrl: string | null,
): Promise<void> {
  if (!logoUrl) return;
  const supabaseUrl = getSupabaseUrlFromUnknown(client);
  const path = parseStoreLogoPathFromPublicUrl(logoUrl, supabaseUrl);
  if (!path) return;
  assertStoreLogoPublicUrlForStore(storeId, logoUrl, supabaseUrl);
  await deleteStoreLogoObject(client, path);
}

/**
 * Delete a store logo from the store-logos bucket.
 * Used for cleanup if store creation fails after logo upload.
 *
 * Rev 2 note: strips query params (e.g., ?t=timestamp) from the URL
 * before extracting the extension, since Supabase Storage URLs may
 * include cache-busting params.
 */
export async function deleteStoreLogo(
  client: SupabaseClient,
  storeId: string,
  logoUrl: string,
): Promise<void> {
  // Strip query params before extracting extension
  const urlWithoutParams = logoUrl.split('?')[0] ?? logoUrl;
  const ext = urlWithoutParams.split('.').pop() ?? 'png';
  const path = `${storeId}/logo.${ext}`;

  const { error } = await client.storage.from(STORE_LOGO_BUCKET).remove([path]);

  // Best-effort cleanup -- don't throw if delete fails
  if (error) console.error('Failed to clean up orphaned logo:', error);
}

/**
 * Map a store-creation error to a translation key. Whitelist-based:
 * unknown errors degrade to admin.createStore.errors.unknown.
 */
export function mapStoreError(err: unknown): StoreErrorKey {
  if (!err || typeof err !== 'object') return 'admin.createStore.errors.unknown';

  if (err instanceof TypeError && /fetch/i.test((err as Error).message ?? '')) {
    return 'admin.createStore.errors.networkFailed';
  }

  const maybe = err as {
    code?: string;
    message?: string;
    name?: string;
    status?: number;
    statusCode?: string;
  };

  if (maybe.name === 'AuthRetryableFetchError') {
    return 'admin.createStore.errors.networkFailed';
  }

  // Unique constraint violation on slug
  if (maybe.code === '23505' && maybe.message?.includes('stores_slug_unique_live')) {
    return 'admin.createStore.errors.slugTaken';
  }

  if (maybe.message?.includes('reserved store slug')) {
    return 'admin.createStore.errors.slugReserved';
  }

  // Generic unique constraint (fallback pattern)
  if (maybe.code === '23505') {
    return 'admin.createStore.errors.slugTaken';
  }

  if (
    maybe.code === '42501' ||
    /merchant approval required|permission denied/i.test(maybe.message ?? '')
  ) {
    return 'admin.createStore.errors.approvalRequired';
  }

  // Storage errors
  if (maybe.statusCode === '413' || maybe.message?.includes('Payload too large')) {
    return 'admin.createStore.errors.logoUploadFailed';
  }

  if (maybe.message?.includes('mime type') || maybe.message?.includes('not allowed')) {
    return 'admin.createStore.errors.logoUploadFailed';
  }

  return 'admin.createStore.errors.unknown';
}
